import { useEffect, useState } from "react";
import Popover, { PopoverOrigin } from "@mui/material/Popover";
import Button from "@mui/material/Button";
import { DateRange, Range, RangeKeyDict } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./DateRangePicker.css";
import dayjs from "dayjs";

export interface DateRangeValue {
    fromDate: string | null;
    toDate: string | null;
}

interface DateRangePickerPopoverProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    value: DateRangeValue;
    minDate?: Date;
    maxDate?: Date;
    initialFocusedRange?: [number, 0 | 1];
    anchorOrigin?: PopoverOrigin;
    transformOrigin?: PopoverOrigin;
    onApply: (value: DateRangeValue) => void;
    onCancel: () => void;
}

const SELECTION_KEY = "selection";

const toDate = (value?: string | null): Date | undefined => (value ? dayjs(value).toDate() : undefined);
const toDateString = (value?: Date): string | null => (value ? dayjs(value).format("YYYY-MM-DD") : null);

const DateRangePickerPopover = ({
    open,
    anchorEl,
    value,
    minDate,
    maxDate,
    initialFocusedRange,
    anchorOrigin,
    transformOrigin,
    onApply,
    onCancel,
}: DateRangePickerPopoverProps) => {
    const [selection, setSelection] = useState<Pick<Range, "startDate" | "endDate">>({
        startDate: toDate(value.fromDate),
        endDate: toDate(value.toDate),
    });

    useEffect(() => {
        if (open) {
            const startDate = toDate(value.fromDate) ?? maxDate ?? new Date();
            setSelection({
                startDate,
                endDate: toDate(value.toDate) ?? startDate,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleChange = (ranges: RangeKeyDict) => {
        const range = ranges[SELECTION_KEY];
        setSelection({ startDate: range.startDate, endDate: range.endDate });
    };

    const handleApply = () => {
        onApply({
            fromDate: toDateString(selection.startDate),
            toDate: toDateString(selection.endDate),
        });
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onCancel}
            anchorOrigin={anchorOrigin ?? { vertical: "bottom", horizontal: "left" }}
            transformOrigin={transformOrigin ?? { vertical: "top", horizontal: "left" }}
            style={{ zIndex: 1500 }}
        >
            <div className="date-range-picker-popover max-w-[95vw] overflow-x-auto">
                <DateRange
                    ranges={[{ startDate: selection.startDate, endDate: selection.endDate, key: SELECTION_KEY }]}
                    onChange={handleChange}
                    months={2}
                    direction="horizontal"
                    moveRangeOnFirstSelection={false}
                    showDateDisplay={false}
                    showMonthAndYearPickers={false}
                    minDate={minDate}
                    maxDate={maxDate}
                    initialFocusedRange={initialFocusedRange}
                    rangeColors={["#406BBE"]}
                />
                <div className="date-range-picker-footer">
                    <Button
                        onClick={onCancel}
                        size="small"
                        sx={{ color: "#406BBE", textTransform: "none", px: 3 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleApply}
                        variant="contained"
                        size="small"
                        sx={{ backgroundColor: "#406BBE", "&:hover": { backgroundColor: "#34508B" }, textTransform: "none", px: 3 }}
                    >
                        Apply
                    </Button>
                </div>
            </div>
        </Popover>
    );
};

export default DateRangePickerPopover;
